/**
 * @author      Dario Casertano <dario@casertano.name>
 * @copyright   Copyright (c) 2021-2023 Casertano Dario – All rights reserved.
 * @license     Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International.
 */

import { reduce } from "lodash";

abstract class Storage {
    constructor(
        protected readonly dbname?: string
    ) {
        if (!dbname) {
            const url = new URL(location.href)

            this.dbname = url.hostname.split('.').reverse().join('.')
        }
    }

    get<T = never>(
        ns: string,
        context: string | null = null,
        key: string | null = null,
        def: T | null = null
    ): T {
        const data = this.getItem(this.path([ns, context, key]))

        return (data ? JSON.parse(data) : def) as T
    }

    set<T = never>(
        ns: string,
        context: string | null = null,
        key: string | null = null,
        value: T
    ): void {
        this.setItem(this.path([ns, context, key]), JSON.stringify(value))
    }

    del(
        ns: string,
        context: string | null = null,
        key: string | null = null
    ): void {
        this.removeItem(this.path([ns, context, key]))
    }

    has(
        ns: string,
        context: string | null = null,
        key: string | null = null
    ): boolean {
        return (this.get(ns, context, key) !== null)
    }

    protected path(parts: (string | null)[]): string {
        return reduce(parts, (args, part): string[] => {
            if (part) {
                args.push(part)
            }

            return args
        }, [`@${this.dbname}`]).join('/')
    }

    protected abstract getItem(key: string): string | null
    protected abstract setItem(key: string, value: string): void
    protected abstract removeItem(key: string): void
}

export class LocalStorage extends Storage {
    protected getItem = (key: string): string | null => localStorage.getItem(key)
    protected setItem = (key: string, value: string): void => localStorage.setItem(key, value)
    protected removeItem = (key: string): void => localStorage.removeItem(key)
}

export class SessionStorage extends Storage {
    protected getItem = (key: string): string | null => sessionStorage.getItem(key)
    protected setItem = (key: string, value: string): void => sessionStorage.setItem(key, value)
    protected removeItem = (key: string): void => sessionStorage.removeItem(key)
}

export const settings = new LocalStorage()
export const session = new SessionStorage()
